import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InvoiceListParams } from "../../../finance-api/ledger/invoices";
import { financeApi } from "../../../finance-api-client";
import type { CreateInvoice, UpdateInvoice } from "../../../types";
import { queryKeys } from "../../query-keys";

export function useInvoices(clubId: string | null, status?: string, params?: InvoiceListParams) {
	return useQuery({
		queryKey: [...queryKeys.clubPortal.invoices(clubId ?? "", status), params ?? {}],
		queryFn: () => financeApi.invoices.list(clubId!, status, params),
		enabled: !!clubId,
		placeholderData: keepPreviousData,
	});
}

export function useMemberInvoices(clubId: string | null, memberId: string) {
	return useQuery({
		queryKey: queryKeys.clubPortal.invoices(clubId ?? "", memberId),
		queryFn: () => financeApi.invoices.listByMember(clubId!, memberId),
		enabled: !!clubId && !!memberId,
	});
}

export function useInvoiceDetail(clubId: string | null, invoiceId: string) {
	return useQuery({
		queryKey: queryKeys.clubPortal.invoiceDetail(clubId ?? "", invoiceId),
		queryFn: () => financeApi.invoices.get(clubId!, invoiceId),
		enabled: !!clubId && !!invoiceId,
	});
}

export function useCreateInvoice(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateInvoice) => financeApi.invoices.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "invoices"],
			}),
	});
}

export function useUpdateInvoice(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ invoiceId, data }: { invoiceId: string; data: UpdateInvoice }) =>
			financeApi.invoices.update(clubId, invoiceId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "invoices"],
			}),
	});
}

export function useDeleteInvoice(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (invoiceId: string) => financeApi.invoices.delete(clubId, invoiceId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "invoices"],
			}),
	});
}

export function useTransitionInvoice(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			invoiceId,
			data,
		}: {
			invoiceId: string;
			data: { status: string; amount_cents?: number; method?: string };
		}) => financeApi.invoices.transition(clubId, invoiceId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "invoices"],
			});
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "payments"],
			});
		},
	});
}

export function useGenerateInvoices(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: { fee_id: string; member_ids?: string[]; due_date: string }) =>
			financeApi.invoices.generate(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: ["club-portal", "clubs", clubId, "invoices"],
			}),
	});
}
